/**
 * 浏览器 WebSocket 管理器（对齐小程序 shopro/utils/websocket.js）
 */
import { WEBSOCKET_URL } from '@/config'

export default class WebSocketManager {
  constructor(options = {}) {
    this.url = options.url || WEBSOCKET_URL
    this.heartbeatInterval = options.heartbeatInterval || 30000
    this.reconnectInterval = options.reconnectInterval || 3000
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5
    this.autoReconnect = options.autoReconnect !== false

    this.socket = null
    this.isConnected = false
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.heartbeatTimer = null
    this.reconnectTimer = null
    this.isReconnecting = false
    this.messageQueue = []
    this.isFlushingQueue = false
    this.eventListeners = {
      open: [],
      message: [],
      error: [],
      close: [],
      reconnect: []
    }
    this.lastPongTime = Date.now()
    this._manualClose = false
  }

  connect() {
    if (this.isConnected || this.isConnecting) {
      return Promise.resolve()
    }
    this.isConnecting = true
    this._manualClose = false

    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(this.url)
        this.bindEvents(resolve, reject)
      } catch (error) {
        this.isConnecting = false
        this.handleError(error)
        reject(error)
      }
    })
  }

  bindEvents(resolve, reject) {
    if (!this.socket) return

    this.socket.onopen = res => {
      this.isConnected = true
      this.isConnecting = false
      this.reconnectAttempts = 0
      this.lastPongTime = Date.now()
      this.startHeartbeat()
      this.flushMessageQueue()
      this.emit('open', res)
      if (resolve) resolve(res)
    }

    this.socket.onmessage = event => {
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data && data.type === 'pong') {
          this.lastPongTime = Date.now()
          return
        }
        this.emit('message', data)
      } catch (e) {
        this.emit('message', event.data)
      }
    }

    this.socket.onerror = err => {
      this.handleError(err)
      if (reject && this.isConnecting) reject(err)
    }

    this.socket.onclose = res => {
      this.isConnected = false
      this.isConnecting = false
      this.stopHeartbeat()
      this.emit('close', res)
      if (this.autoReconnect && !this._manualClose && !this.isReconnecting) {
        this.reconnect()
      }
    }
  }

  send(data) {
    return new Promise((resolve, reject) => {
      try {
        const message = typeof data === 'string' ? data : JSON.stringify(data)
        if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(message)
          resolve({ ok: true })
        } else {
          this.addToQueue(data)
          resolve({ queued: true })
        }
      } catch (error) {
        this.addToQueue(data, 1)
        reject(error)
      }
    })
  }

  addToQueue(data, retryCount = 0) {
    if (retryCount >= 3) return
    this.messageQueue.push({ data, timestamp: Date.now(), retryCount })
  }

  async flushMessageQueue() {
    if (this.isFlushingQueue || !this.messageQueue.length) return
    this.isFlushingQueue = true
    const queue = [...this.messageQueue]
    this.messageQueue = []
    for (const item of queue) {
      try {
        await this.send(item.data)
      } catch (e) {
        this.addToQueue(item.data, item.retryCount + 1)
      }
    }
    this.isFlushingQueue = false
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected) return
      const now = Date.now()
      if (now - this.lastPongTime > this.heartbeatInterval * 2) return
      this.send({ type: 'ping', timestamp: now }).catch(() => {})
    }, this.heartbeatInterval)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  reconnect() {
    if (this.isReconnecting || this.isConnected || this.isConnecting) return
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return

    this.isReconnecting = true
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.isReconnecting = false
      this.connect()
        .then(() => {
          this.emit('reconnect', { attempts: this.reconnectAttempts })
        })
        .catch(() => {
          this.isReconnecting = false
          if (this.autoReconnect && !this._manualClose) this.reconnect()
        })
    }, this.reconnectInterval)
  }

  handleError(error) {
    this.emit('error', error)
  }

  close() {
    this._manualClose = true
    this.autoReconnect = false
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.socket) {
      try {
        this.socket.close(1000, '正常关闭')
      } catch (e) {
        // ignore
      }
      this.socket = null
    }
    this.isConnected = false
    this.isConnecting = false
    this.isReconnecting = false
  }

  on(event, handler) {
    if (!this.eventListeners[event]) this.eventListeners[event] = []
    this.eventListeners[event].push(handler)
  }

  off(event, handler) {
    if (!this.eventListeners[event]) return
    this.eventListeners[event] = this.eventListeners[event].filter(fn => fn !== handler)
  }

  emit(event, payload) {
    ;(this.eventListeners[event] || []).forEach(fn => {
      try {
        fn(payload)
      } catch (e) {
        // ignore listener errors
      }
    })
  }

  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      messageQueueLength: this.messageQueue.length,
      url: this.url
    }
  }
}
